import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOHelmetProps {
  title: string;
  description: string;
  canonical?: string;
  image?: string;
  schemaType?: 'Organization' | 'RealEstateListing' | 'RealEstateAgent' | 'LocalBusiness' | 'Article';
  schemaData?: Record<string, any>;
  keywords?: string[];
  noindex?: boolean;
}

/**
 * Componente che gestisce i metadati SEO per ogni pagina
 */
const SEOHelmet: React.FC<SEOHelmetProps> = ({
  title,
  description,
  canonical,
  image = '/images/joh-logo-social.jpg',
  schemaType,
  schemaData,
  keywords = [],
  noindex = false,
}) => {
  const siteUrl = 'https://j-o-h.com'; // Sostituire con l'URL effettivo del sito
  const fullUrl = canonical ? `${siteUrl}${canonical}` : siteUrl;
  const fullImageUrl = image.startsWith('http') ? image : `${siteUrl}${image}`;
  
  // Schema.org structured data
  const generateSchemaData = () => {
    let schema = {};
    
    const baseSchema = {
      '@context': 'https://schema.org',
    };
    
    if (schemaType === 'Organization') {
      schema = {
        ...baseSchema,
        '@type': 'Organization',
        name: 'J-O-H',
        url: siteUrl,
        logo: `${siteUrl}/logo.png`,
        contactPoint: {
          '@type': 'ContactPoint',
          telephone: schemaData?.telephone || '+39 123 456 7890',
          contactType: 'customer service'
        },
        sameAs: [
          'https://facebook.com/joh', // Sostituire con i link effettivi
          'https://instagram.com/joh',
        ],
        ...schemaData
      };
    } else if (schemaType === 'RealEstateListing') {
      schema = {
        ...baseSchema,
        '@type': 'RealEstateListing',
        name: schemaData?.name || title,
        description: schemaData?.description || description,
        image: fullImageUrl,
        url: fullUrl,
        address: {
          '@type': 'PostalAddress',
          addressLocality: schemaData?.city || 'Firenze',
          addressRegion: schemaData?.region || 'Italia',
          addressCountry: 'IT'
        },
        offers: {
          '@type': 'Offer',
          price: schemaData?.price || '',
          priceCurrency: 'EUR',
          availability: 'https://schema.org/InStock'
        },
        ...schemaData
      };
    } else if (schemaType === 'LocalBusiness') {
      schema = {
        ...baseSchema,
        '@type': 'LocalBusiness',
        name: 'J-O-H',
        image: fullImageUrl,
        url: siteUrl,
        telephone: schemaData?.telephone || '+39 123 456 7890',
        address: {
          '@type': 'PostalAddress',
          streetAddress: schemaData?.streetAddress || 'Via Roma, 1',
          addressLocality: schemaData?.city || 'Firenze',
          addressRegion: schemaData?.region || 'Italia',
          postalCode: schemaData?.postalCode || '50123',
          addressCountry: 'IT'
        },
        geo: {
          '@type': 'GeoCoordinates',
          latitude: schemaData?.latitude || '43.7695604',
          longitude: schemaData?.longitude || '11.2558136'
        },
        openingHoursSpecification: [
          {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: [
              'Monday',
              'Tuesday',
              'Wednesday',
              'Thursday',
              'Friday'
            ],
            opens: '09:00',
            closes: '18:00'
          }
        ],
        ...schemaData
      };
    } else if (schemaType === 'Article') {
      schema = {
        ...baseSchema,
        '@type': 'Article',
        headline: title,
        image: fullImageUrl,
        author: {
          '@type': 'Person',
          name: schemaData?.author || 'J-O-H Team'
        },
        publisher: {
          '@type': 'Organization',
          name: 'J-O-H',
          logo: {
            '@type': 'ImageObject',
            url: `${siteUrl}/logo.png`
          }
        },
        datePublished: schemaData?.datePublished || new Date().toISOString(),
        dateModified: schemaData?.dateModified || new Date().toISOString(),
        ...schemaData
      };
    }
    
    return JSON.stringify(schema);
  };
  
  return (
    <Helmet>
      {/* Titolo e meta tag base */}
      <title>{title} | J-O-H</title>
      <meta name="description" content={description} />
      
      {/* Canonical link */}
      {canonical && <link rel="canonical" href={fullUrl} />}
      
      {/* Keywords */}
      {keywords.length > 0 && (
        <meta name="keywords" content={keywords.join(', ')} />
      )}
      
      {/* Robots */}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImageUrl} />
      <meta property="og:site_name" content="J-O-H" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImageUrl} />
      
      {/* Favicon */}
      <link rel="icon" href="/favicon.ico" />
      <link rel="apple-touch-icon" href="/logo192.png" />
      
      {/* Schema.org structured data */}
      {schemaType && (
        <script type="application/ld+json">
          {generateSchemaData()}
        </script>
      )}
    </Helmet>
  );
};

export default SEOHelmet; 